import { useState, useEffect } from "react";
import { Filter, AlertCircle, AlertTriangle, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { ParsedOrder } from "@/lib/bulkOrderUtils";
import { RejectedOrdersList } from "./RejectedOrdersList";
import { DuplicateComparisonDialog } from "./DuplicateComparisonDialog";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface OrderReviewTableEnhancedProps {
  orders: ParsedOrder[];
  summary: any;
  onImport: (selectedOrders: ParsedOrder[]) => void;
  onBack: () => void;
  isImporting?: boolean;
  onSelectionChange?: (count: number, selectedOrders: ParsedOrder[]) => void;
}

export function OrderReviewTableEnhanced({ 
  orders: initialOrders, 
  summary, 
  onImport, 
  onBack, 
  isImporting = false,
  onSelectionChange
}: OrderReviewTableEnhancedProps) {
  const { t } = useLanguage();
  const [orders, setOrders] = useState(initialOrders);
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    new Set(orders.map((_, idx) => idx).filter(idx => 
      orders[idx].valid && 
      !orders[idx].isDuplicate && 
      orders[idx].rowState !== 'rejected'
    ))
  );
  const [filterState, setFilterState] = useState<string>('all');
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [selectedDuplicateOrder, setSelectedDuplicateOrder] = useState<ParsedOrder | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const toggleSelection = (index: number) => {
    if (orders[index].isDuplicate || orders[index].rowState === 'rejected') {
      return;
    }
    
    const newSet = new Set(selectedIndices);
    if (newSet.has(index)) {
      newSet.delete(index);
    } else {
      newSet.add(index);
    }
    setSelectedIndices(newSet);
  };

  const toggleSelectAll = () => {
    const selectableOrders = orders.filter(o => 
      !o.isDuplicate && 
      o.rowState !== 'rejected'
    );
    
    if (selectedIndices.size === selectableOrders.length) {
      setSelectedIndices(new Set());
    } else {
      const newSet = new Set<number>();
      orders.forEach((order, idx) => {
        if (!order.isDuplicate && order.rowState !== 'rejected') {
          newSet.add(idx);
        }
      });
      setSelectedIndices(newSet);
    }
  };

  const handleImport = () => {
    const selectedOrders = orders.filter((_, idx) => selectedIndices.has(idx));
    onImport(selectedOrders);
  };

  const handleUpdateOrder = (index: number, updatedOrder: ParsedOrder) => {
    const newOrders = [...orders];
    newOrders[index] = updatedOrder;
    setOrders(newOrders);
  };

  const handleRejectOrder = (index: number) => {
    const newOrders = [...orders];
    newOrders[index] = { ...newOrders[index], rowState: 'rejected' };
    setOrders(newOrders);
    
    const newSet = new Set(selectedIndices);
    newSet.delete(index);
    setSelectedIndices(newSet);
  };

  const handleRestoreOrder = (index: number) => {
    const newOrders = [...orders];
    const order = newOrders[index];
    
    if (order.isDuplicate) {
      newOrders[index] = { ...order, rowState: 'duplicate' };
    } else if (order.errors.length > 0) {
      newOrders[index] = { ...order, rowState: 'error' };
    } else if (order.warnings.length > 0) {
      newOrders[index] = { ...order, rowState: 'warning' };
    } else {
      newOrders[index] = { ...order, rowState: 'valid' };
    }
    
    setOrders(newOrders);
  };

  const handleRestoreAll = () => {
    const newOrders = orders.map(order => {
      if (order.rowState === 'rejected') {
        if (order.isDuplicate) {
          return { ...order, rowState: 'duplicate' as const };
        } else if (order.errors.length > 0) {
          return { ...order, rowState: 'error' as const };
        } else if (order.warnings.length > 0) {
          return { ...order, rowState: 'warning' as const };
        } else {
          return { ...order, rowState: 'valid' as const };
        }
      }
      return order;
    });
    setOrders(newOrders);
  };

  const handleDeselectInvalid = () => {
    const newSet = new Set<number>();
    orders.forEach((order, idx) => {
      if (order.valid && !order.isDuplicate && order.rowState !== 'rejected') {
        newSet.add(idx);
      }
    });
    setSelectedIndices(newSet);
  };

  const handleCompareDuplicate = (order: ParsedOrder) => {
    setSelectedDuplicateOrder(order);
    setDuplicateDialogOpen(true);
  };

  const getFilteredOrders = () => {
    if (filterState === 'all') return orders.map((o, i) => ({order: o, index: i}));
    if (filterState === 'valid') return orders.map((o, i) => ({order: o, index: i})).filter(({order}) => order.valid && !order.isDuplicate);
    if (filterState === 'warnings') return orders.map((o, i) => ({order: o, index: i})).filter(({order}) => order.rowState === 'warning');
    if (filterState === 'errors') return orders.map((o, i) => ({order: o, index: i})).filter(({order}) => order.rowState === 'error');
    if (filterState === 'duplicates') return orders.map((o, i) => ({order: o, index: i})).filter(({order}) => order.isDuplicate);
    return orders.map((o, i) => ({order: o, index: i}));
  };

  const stats = {
    total: orders.length,
    valid: orders.filter(o => o.valid && !o.isDuplicate && o.rowState !== 'rejected').length,
    warnings: orders.filter(o => o.rowState === 'warning').length,
    errors: orders.filter(o => o.rowState === 'error').length,
    duplicates: orders.filter(o => o.isDuplicate).length,
    rejected: orders.filter(o => o.rowState === 'rejected').length
  };

  const getCardClasses = (order: ParsedOrder) => {
    if (order.rowState === 'rejected') return 'border-destructive bg-destructive/5 opacity-60';
    if (order.rowState === 'error') return 'border-destructive/50 bg-destructive/5';
    if (order.isDuplicate) return 'border-orange-500/50 bg-orange-500/5';
    if (order.rowState === 'warning') return 'border-yellow-500/50 bg-yellow-500/5';
    return 'border-border';
  };

  // Notify parent of selection changes
  useEffect(() => {
    const selected = orders.filter((_, idx) => selectedIndices.has(idx));
    onSelectionChange?.(selectedIndices.size, selected);
  }, [selectedIndices, orders, onSelectionChange]);

  // Compact order card with inline editing capability
  const CompactOrderCard = ({ order, index }: { order: ParsedOrder; index: number }) => {
    const isSelected = selectedIndices.has(index);
    const canSelect = !order.isDuplicate && order.rowState !== 'rejected';
    const isEditing = editingIndex === index;
    
    // Local state for inline editing
    const [localOrder, setLocalOrder] = useState(order);
    
    // Reset local order when parent order changes or editing stops
    useEffect(() => {
      setLocalOrder(order);
    }, [order]);
    
    const handleSave = () => {
      handleUpdateOrder(index, { ...localOrder, isEdited: true });
      setEditingIndex(null);
    };
    
    const handleCancel = () => {
      setLocalOrder(order);
      setEditingIndex(null);
    };

    const getStatusText = () => {
      if (order.rowState === 'error') return t("bulk.statuses.error");
      if (order.rowState === 'warning') return t("bulk.statuses.warning");
      if (order.isDuplicate) return t("bulk.statuses.duplicate");
      if (order.rowState === 'rejected') return t("bulk.statuses.rejected");
      if (isEditing) return t("bulk.statuses.editing");
      if (order.valid) return t("bulk.statuses.ready");
      return null;
    };

    const getStatusColor = () => {
      if (order.rowState === 'error' || order.rowState === 'rejected') return 'text-destructive';
      if (order.rowState === 'warning') return 'text-yellow-600 dark:text-yellow-400';
      if (order.isDuplicate) return 'text-orange-600 dark:text-orange-400';
      if (isEditing) return 'text-primary';
      return 'text-muted-foreground';
    };

    return (
      <div className={cn(
        "p-3 rounded-lg border transition-colors",
        isEditing && "border-primary ring-1 ring-primary/20",
        !isEditing && getCardClasses(order)
      )}>
        {/* Header: Checkbox + Name + Status */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Checkbox
              checked={isSelected}
              onCheckedChange={() => canSelect && toggleSelection(index)}
              disabled={!canSelect || isEditing}
              className="flex-shrink-0"
            />
            {isEditing ? (
              <Input
                value={localOrder.customerName}
                onChange={(e) => setLocalOrder({...localOrder, customerName: e.target.value})}
                className="h-7 text-sm font-medium flex-1"
                placeholder={t("orders.fields.customerName")}
              />
            ) : (
              <span className="font-medium text-sm truncate">
                {order.customerName || `${t("orders.order")} ${index + 1}`}
              </span>
            )}
          </div>
          <span className={cn("text-xs flex-shrink-0 ml-2", getStatusColor())}>
            {getStatusText()}
          </span>
        </div>
        
        {/* Editable or display fields */}
        {isEditing ? (
          <div className="space-y-2 pl-6">
            {/* Phone */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                {t("orders.fields.phone")}
              </label>
              <Input
                value={localOrder.customerPhone || ''}
                onChange={(e) => setLocalOrder({...localOrder, customerPhone: e.target.value})}
                className="h-7 text-sm font-mono"
                placeholder="5XX XXX XXX"
              />
            </div>
            
            {/* Address */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                {t("orders.fields.address")}
              </label>
              <Input
                value={localOrder.address || ''}
                onChange={(e) => setLocalOrder({...localOrder, address: e.target.value})}
                className="h-7 text-sm"
                placeholder={t("orders.fields.address")}
              />
            </div>
            
            {/* Total Price */}
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">
                {t("orders.fields.total")}
              </label>
              <Input
                type="number"
                value={localOrder.totalPrice || ''}
                onChange={(e) => setLocalOrder({...localOrder, totalPrice: Number(e.target.value) || null})}
                className="h-7 text-sm"
                placeholder="0.00"
              />
            </div>
            
            {/* Save/Cancel buttons */}
            <div className="flex gap-2 pt-2">
              <Button size="sm" onClick={handleSave} className="h-7 text-xs">
                <Check className="h-3 w-3 mr-1" />
                {t("bulk.actions.save")}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleCancel} className="h-7 text-xs">
                {t("bulk.actions.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          <>
            {/* Compact field list - matching OrderReviewSummary pattern */}
            <div className="space-y-1 text-sm pl-6">
              {order.customerPhone && (
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("orders.fields.phone")}</span>
                  <span className="text-foreground">{order.customerPhone}</span>
                </div>
              )}
              <div className="flex justify-between text-muted-foreground">
                <span>{t("orders.table.products")}</span>
                <span className="text-foreground">{order.products?.length || 0}</span>
              </div>
              {order.totalPrice != null && order.totalPrice > 0 && (
                <div className="flex justify-between text-muted-foreground">
                  <span>{t("orders.fields.total")}</span>
                  <span className="text-foreground font-medium">{order.totalPrice.toFixed(2)} ₾</span>
                </div>
              )}
            </div>

            {/* Error message - single line, minimal */}
            {order.errors.length > 0 && (
              <p className="text-xs text-destructive mt-2 pl-6 flex items-center gap-1">
                <AlertCircle className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{order.errors[0].message}</span>
              </p>
            )}
            
            {/* Warning message - single line */}
            {order.warnings.length > 0 && order.errors.length === 0 && (
              <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-2 pl-6 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{order.warnings[0]}</span>
              </p>
            )}

            {/* Actions row - minimal */}
            <div className="flex items-center gap-3 mt-2 pl-6">
              <button 
                className="text-xs text-primary hover:underline"
                onClick={() => setEditingIndex(index)}
              >
                {t("bulk.actions.edit")}
              </button>
              
              {order.isDuplicate && (
                <button 
                  className="text-xs text-orange-600 hover:underline"
                  onClick={() => handleCompareDuplicate(order)}
                >
                  {t("bulk.actions.compare")}
                </button>
              )}
              
              {order.rowState !== 'rejected' && (
                <button 
                  className="text-xs text-destructive hover:underline"
                  onClick={() => handleRejectOrder(index)}
                >
                  {t("bulk.actions.reject")}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full space-y-3">
      {/* Summary line - quiet, text-based */}
      <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
        <span className="flex items-center gap-1">
          <Check className="h-3.5 w-3.5 text-green-600" />
          {stats.valid} {t("bulk.statuses.ready").toLowerCase()}
        </span>
        {stats.warnings > 0 && (
          <span className="text-yellow-600 dark:text-yellow-400">
            {stats.warnings} {t("bulk.statuses.warning").toLowerCase()}
          </span>
        )}
        {stats.errors > 0 && (
          <span className="text-destructive">
            {stats.errors} {t("bulk.statuses.error").toLowerCase()}
          </span>
        )}
        {stats.duplicates > 0 && (
          <span className="text-orange-600 dark:text-orange-400">
            {stats.duplicates} {t("bulk.statuses.duplicate").toLowerCase()}
          </span>
        )}
      </div>

      {/* Compact selection bar */}
      <div className="flex items-center justify-between gap-2 py-2 border-b border-border/50">
        <div className="flex items-center gap-2">
          <Checkbox
            checked={selectedIndices.size > 0 && selectedIndices.size === stats.valid}
            onCheckedChange={toggleSelectAll}
          />
          <span className="text-xs text-muted-foreground">
            {selectedIndices.size}/{stats.valid}
          </span>
        </div>

        <Select value={filterState} onValueChange={setFilterState}>
          <SelectTrigger className="h-7 w-[110px] text-xs">
            <Filter className="h-3 w-3 mr-1" />
            <SelectValue placeholder={t("bulk.review.filterAll")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("bulk.review.filterAll")}</SelectItem>
            <SelectItem value="valid">{t("bulk.review.filterValid")}</SelectItem>
            <SelectItem value="warnings">{t("bulk.statuses.warning")}</SelectItem>
            <SelectItem value="errors">{t("bulk.review.filterErrors")}</SelectItem>
            <SelectItem value="duplicates">{t("bulk.review.filterDuplicates")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Rejected orders */}
      <RejectedOrdersList
        orders={orders}
        onRestoreAll={handleRestoreAll}
        onRestore={handleRestoreOrder}
      />

      {/* Scrollable order cards */}
      <ScrollArea className="flex-1 -mx-4 px-4">
        <div className="space-y-2 pb-2">
          {getFilteredOrders().map(({ order, index }) => (
            <CompactOrderCard key={index} order={order} index={index} />
          ))}
        </div>
      </ScrollArea>

      {/* Tips - minimal */}
      <p className="text-[10px] text-muted-foreground">
        {t("bulk.tips.editReject")}
      </p>

      {selectedDuplicateOrder && (
        <DuplicateComparisonDialog
          open={duplicateDialogOpen}
          onOpenChange={setDuplicateDialogOpen}
          newOrder={selectedDuplicateOrder}
        />
      )}
    </div>
  );
}
